/**
 * PromoSection - Renders promotional cards
 *
 * Follows the Challenge promotions pattern with image + title + description + URL.
 * Supports 2-up or 3-up layouts.
 */

import { SurfacePromoItemResponse } from "@/hooks/hub/useGetHub";
import "./PromoSection.scss";

interface PromoSectionProps {
    items?: SurfacePromoItemResponse[];
    layout?: "2-up" | "3-up";
}

/**
 * Validates URL is safe (http/https or relative path, not javascript:)
 */
const isValidUrl = (url?: string): boolean => {
    if (!url) return false;
    return url.startsWith("http://") || url.startsWith("https://") || url.startsWith("/");
};

export const PromoSection = ({ items, layout = "3-up" }: PromoSectionProps) => {
    if (!items || items.length === 0) return null;

    return (
        <div className={`promo-section promo-section--${layout}`}>
            <div className="promo-section__grid">
                {items.map((item, index) => {
                    const hasValidUrl = isValidUrl(item.url);
                    const isExternal = item.url?.startsWith("http");
                    const CardWrapper = hasValidUrl ? "a" : "div";

                    return (
                        <CardWrapper
                            key={item._id || index}
                            className="promo-section__card"
                            {...(hasValidUrl && {
                                href: item.url,
                                target: isExternal ? "_blank" : undefined,
                                rel: isExternal ? "noopener noreferrer" : undefined,
                            })}
                        >
                            {item.imageUrl &&
                                <div className="promo-section__image-wrapper">
                                    <img
                                        src={item.imageUrl}
                                        alt={item.title || "Promotion"}
                                        className="promo-section__image"
                                    />
                                </div>
                            }
                            {(item.title || item.description) &&
                                <div className="promo-section__content">
                                    {item.title &&
                                        <h3 className="promo-section__title">{item.title}</h3>
                                    }
                                    {item.description &&
                                        <p className="promo-section__description">{item.description}</p>
                                    }
                                </div>
                            }
                        </CardWrapper>
                    );
                })}
            </div>
        </div>
    );
};
